import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import {
  MAT_DIALOG_DATA,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { select, Store } from '@ngrx/store';
import { ErrorSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import {
  AGENCY_STAGES,
  AppState,
  DocumentsStore,
  DocumentTemplatesActions,
  ItemStore,
  LeadBankStore,
  ProductSchemasStore,
  selectMyDealsWorkflowsGrouped,
  selectSchemaViewBySid,
  TermSheetStore,
  UserStore,
  WorkflowActions,
} from '@qtek/libs/store';
import { TemplateSelectorComponent } from '@qtek/libs/templates';
import {
  TranslationCoreModule,
  TranslationCoreService,
} from '@qtek/libs/translation-core';
import { DateHourPickerComponent, ErrorComponent } from '@qtek/libs/ui';
import {
  DialogActionsDirective,
  DialogComponent,
  DialogContentDirective,
  DialogTitleDirective,
} from '@qtek/shared/components';
import {
  CurrencyInputDirective,
  PercentInputDirective,
} from '@qtek/shared/directives';
import {
  BaseEnumValue,
  DocMetaResponse,
  DocNode,
  DocumentTemplate,
  DocumentType,
  HTTP_BASE_URL,
  Item,
  ItemTypeBitMasks,
  NDA_WORKFLOW_OPTIONS,
  NdaWorkflow,
  SchemaView,
  TemplateTypes,
} from '@qtek/shared/models';
import { FileSizePipe, SortPipe } from '@qtek/shared/pipes';
import { loadPdfjsWorker } from '@qtek/shared/utils';
import { format, isValid, parse } from 'date-fns';
import { getDocument, PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  EMPTY,
  forkJoin,
  from,
  merge,
  Observable,
  of,
  race,
  Subject,
  timer,
} from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  takeLast,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  buildTermSheetPatch,
  createTermSheetForm,
  TERM_SHEET_FIELD_MAP,
  TermSheetFormControls,
} from './term-sheet-form.utils';

loadPdfjsWorker();

interface SchemaEnumItem extends BaseEnumValue {
  id: string;
  label?: string;
  description?: string;
  name?: string;
}

interface LeadBankOption {
  acnId?: string;
  name?: string;
  email?: string;
  domains?: string[];
  lgoImg?: string;
  raw?: any;
}

type TermSheetSelectField =
  | 'paymentFrequency'
  | 'index'
  | 'rateResetFrequency'
  | 'syndicationType'
  | 'underwritingType';

const TERM_SHEET_EXTRACTION_TIMER = 120000; // 120 seconds — term sheet extraction schema is ~48 fields; multi-facility docs (3+ facilities) can push 60–90s on Bedrock
const CREATE_DEAL_MYSID = 'create_deal';

export const createParticipantDialogConfig: MatDialogConfig = {
  panelClass: ['fullscreenMd', 'create-participant-dialog'],
  width: '50vw',
  maxWidth: '80vw',
};

@Component({
  selector: 'qt-create-participant-dialog',
  templateUrl: './create-participant-dialog.component.html',
  styleUrls: ['./create-participant-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    TranslationCoreModule,
    DialogComponent,
    DialogActionsDirective,
    DialogTitleDirective,
    DialogContentDirective,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatButtonModule,
    MatOptionModule,
    FlexLayoutModule,
    ErrorComponent,
    DateHourPickerComponent,
    CurrencyInputDirective,
    PercentInputDirective,
    SortPipe,
    FileSizePipe,
    TemplateSelectorComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    UpperCasePipe,
    MatAutocompleteModule,
    MatTooltipModule,
  ],
})
export class CreateParticipantDialogComponent implements OnInit, OnDestroy {
  @ViewChild('termSheetInput') termSheetInput?: ElementRef<HTMLInputElement>;
  @ViewChild('termSheetPreviewContainer')
  termSheetPreviewContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('termSheetErrorMessage')
  termSheetErrorMessage?: ElementRef<HTMLDivElement>;

  private readonly injector = inject(Injector);
  private dialogRef = inject(MatDialogRef<CreateParticipantDialogComponent>);
  private data = inject<{
    isLeadBank?: boolean;
    pid?: string;
    stg?: number;
    entryMode?: 'manual' | 'upload';
  }>(MAT_DIALOG_DATA, { optional: true });
  private router = inject(Router);
  private translateService = inject(TranslationCoreService);
  private store = inject(Store<AppState>);
  private readonly userStore = inject(UserStore);
  private readonly schemasStore = inject(ProductSchemasStore);
  private readonly leadBankStore = inject(LeadBankStore);
  private readonly termSheetStore = inject(TermSheetStore);
  private readonly documentsStore = inject(DocumentsStore);
  private readonly itemStore = inject(ItemStore);
  private readonly isAgency = AGENCY_STAGES.includes(this.data?.stg);
  private readonly termSheetDocMeta$ = toObservable(
    this.termSheetStore.docMeta
  );
  private cdr = inject(ChangeDetectorRef);
  private baseUrl = inject(HTTP_BASE_URL, { optional: true }) || '';
  private snackBar = inject(SnackBarService);
  private destroy$ = new Subject<void>();
  private isProcessingPanelActive = false;
  private termSheetPdfDocument: PDFDocumentProxy | null = null;
  private termSheetPreviewUrl: string | null = null;
  private readonly baseDialogWidth = '50vw';
  private readonly detailsDialogWidth = '75vw';

  // schemas
  protected products$: Observable<SchemaEnumItem[]> = of([]);
  industries$: Observable<SchemaEnumItem[]>;
  protected readonly productIcons = [
    'construction',
    'pending_actions',
    'credit_card',
    'currency_exchange',
  ];

  nameControl = new FormControl<string>('', [Validators.required]);
  productControl = new FormControl<string>('', [Validators.required]);
  industryControl = new FormControl<string>('', [Validators.required]);
  folderTemplateControl = new FormControl<DocumentTemplate | null>(null);
  purchaseAmountControl = new FormControl<number | string | null>(null, [
    Validators.required,
  ]);
  private readonly defaultInvestmentHint =
    'Enter your purchase amount for this buy-side participation. Values can be updated later.';
  investmentHint = '';

  // Lead bank
  private readonly leadBankOptionValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return this.isLeadBankOption(value) ? null : { invalidValue: true };
  };
  leadBankControl = new FormControl<LeadBankOption | string | null>(null, [
    Validators.required,
    this.leadBankOptionValidator,
  ]);
  leadBankEmailControl: FormControl<string> = new FormControl<string>('');
  leadBankGroup: FormGroup = new FormGroup({
    leadBank: this.leadBankControl,
    leadBankEmail: this.leadBankEmailControl,
  });
  leadBankValidationMessage = new BehaviorSubject<string | null>(null);
  suggestedEmails: string[] = [];
  correctedEmail: string | null = null;
  leadBankEmailValid = false;
  selectedLeadBank: LeadBankOption | null = null;
  private leadBankDomainMismatch = false;
  private readonly invalidEmailKey = 'FTR_CUSTOMER_INVITE_INVALID_EMAIL';
  private readonly domainMismatchKey =
    'GNR_EMAIL_DOMAIN_NOT_MATCHING_ORGANIZATION';

  termSheetForm = createTermSheetForm();
  termSheetSelectOptions: Record<string, SchemaEnumItem[]> = {};

  termSheetFiles: File[] = [];
  termSheetDragCounter = 0;
  termSheetUploadError = false;
  termSheetUploadBlocked = false;
  termSheetWrongDocument = false;
  termSheetStep: 'base' | 'processing' | 'details' = 'base';
  termSheetDocMeta: DocMetaResponse | null = null;
  termSheetData: Record<string, string | null> | null = null;
  termSheetDocId: string | null = null;
  termSheetMysid: string | null = null;
  isTermSheetUploading$ = new BehaviorSubject(false);
  termSheetPreviewLoading = false;
  termSheetPreviewError = false;

  saving$: Observable<boolean> = of(false);
  private leadBankSaving$ = new BehaviorSubject(false);
  leadBanks$!: Observable<LeadBankOption[]>;
  leadBanksLoading$!: Observable<boolean>;
  validationLoading$!: Observable<boolean>;
  templateTypes = TemplateTypes;
  private mainCompanyId: string | null = null;

  filteredLeadBanks$: Observable<LeadBankOption[]> = of([]);

  /** Lead bank mode (Sales Pipeline): hide Bank & Contact, use ItemActions.createItem */
  get isLeadBank(): boolean {
    return !!this.data?.isLeadBank;
  }

  /** Manual-only mode: user explicitly chose Manual Entry, so hide the Term Sheet upload block. */
  get isManualOnly(): boolean {
    return this.data?.entryMode === 'manual';
  }

  // Lead bank: NDA + investment fields (same as create-item)
  amountGroup: FormGroup = new FormGroup(
    {
      originalAmount: new FormControl<number | null>(null, [
        // Validators.required,
      ]),
      agentHold: new FormControl<number | null>(null, [
        // Validators.required
      ]),
      minHold: new FormControl<number | null>(null, [
        // Validators.required,
        // Validators.min(0),
      ]),
    }
    // Validators.required
  );
  ndaWorkflowControl = new FormControl<string>(NdaWorkflow.Personal, [
    Validators.required,
  ]);
  ndaTemplateControl = new FormControl<DocumentTemplate | null>(null);
  workflowTemplateControl = new FormControl<string[]>([], {
    nonNullable: true,
  });
  ndaWorkflowOptions = NDA_WORKFLOW_OPTIONS;
  workflowTemplateGroups$ = this.store.select(selectMyDealsWorkflowsGrouped);

  constructor() {
    effect(() => {
      const mainCompany = this.userStore.mainCompany();
      this.mainCompanyId = mainCompany?._id ?? null;
      if (
        this.selectedLeadBank?.acnId &&
        this.mainCompanyId &&
        this.selectedLeadBank.acnId === this.mainCompanyId
      ) {
        this.selectedLeadBank = null;
        this.leadBankControl.setValue(null);
      }
    });
  }

  ngOnInit(): void {
    this.resolveInvestmentHint();
    this.store.dispatch(
      DocumentTemplatesActions.connectDocumentTemplates({
        payload: { view: 'dtl' },
      })
    );
    this.store.dispatch(WorkflowActions.subscribeMyDealsWorkflows());

    this.saving$ = this.isLeadBank
      ? this.leadBankSaving$.asObservable()
      : toObservable(this.leadBankStore.submitLoading, {
          injector: this.injector,
        });

    if (this.isLeadBank) {
      this.leadBankControl.clearValidators();
      this.leadBankControl.updateValueAndValidity();
      this.watchNdaWorkflow();

      this.itemStore.connect({
        op: 'fast_query',
        mysid: CREATE_DEAL_MYSID,
        prms: { fastView: 'itemsView' },
        q: [{ k: 'stg', op: this.isAgency ? 'EQ' : 'NQ', v: [500, 600] }],
      });

      this.itemStore.create$
        .pipe(
          filter(
            ({ res, mysid }) =>
              mysid === CREATE_DEAL_MYSID &&
              (res?.name ?? '').trim() === (this.nameControl.value ?? '').trim()
          ),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe(({ res }) => {
          this.itemStore.disconnect(CREATE_DEAL_MYSID);
          const url = this.isAgency ? 'itms/agency' : 'itms/originations';
          this.router
            .navigate([url, 'deal', btoa(res._id)], {
              queryParams: { prevUrl: this.router.url },
            })
            .then(() => this.dialogRef.close());
        });
    } else {
      this.leadBankStore.resetSubmit();
      toObservable(this.leadBankStore.submitResult, { injector: this.injector })
        .pipe(
          filter(result => result !== null),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe(payload =>
          this.dialogRef.close({ payload, dealName: this.nameControl.value })
        );
      toObservable(this.leadBankStore.submitError, { injector: this.injector })
        .pipe(
          filter(err => err !== null),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe(error => {
          const msg = this.extractErrorMessage(error);
          this.setEmailError(msg || this.getInvalidEmailMessage());
          this.leadBankEmailValid = false;
        });
    }

    this.loadSchemas();
    if (!this.isManualOnly) {
      this.setupTermSheetSchemaOptions();
    }
    if (!this.isLeadBank) {
      this.setupLeadBankFilters();
    }
    if (!this.isManualOnly) {
      this.setupTermSheetUploadGate();
      toObservable(this.documentsStore.corporateNda, {
        injector: this.injector,
      })
        .pipe(
          filter(corporateNda => !!corporateNda?._id),
          takeUntil(this.destroy$)
        )
        .subscribe((corporateNda: DocNode | null) => {
          this.resolveTermSheetDocId(corporateNda._id);
        });
    }
  }

  ngOnDestroy(): void {
    this.store.dispatch(DocumentTemplatesActions.disconnectDocumentTemplates());
    this.store.dispatch(WorkflowActions.unsubscribeMyDealsWorkflows());
    this.clearTermSheetUploadState();
    if (this.termSheetMysid) {
      this.termSheetStore.disconnect(this.termSheetMysid);
    }
    if (this.isLeadBank) {
      this.itemStore.disconnect(CREATE_DEAL_MYSID);
    }
    this.isTermSheetUploading$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private watchNdaWorkflow(): void {
    this.ndaWorkflowControl.valueChanges
      .pipe(startWith(this.ndaWorkflowControl.value), takeUntil(this.destroy$))
      .subscribe(value => {
        if (value === NdaWorkflow.Personal) {
          this.ndaTemplateControl.setValidators([Validators.required]);
        } else {
          this.ndaTemplateControl.clearValidators();
          this.ndaTemplateControl.setValue(null);
        }
        this.ndaTemplateControl.updateValueAndValidity();
      });
  }

  private loadSchemas(): void {
    this.products$ = this.store.pipe(
      select(selectSchemaViewBySid('io.synply.v1.type.product')),
      filter(Boolean),
      first(),
      map(schema =>
        Object.entries(schema?.graphEnum?.vals ?? {}).map(([id, val]) => ({
          id,
          ...val,
        }))
      )
    );

    this.industries$ = this.store.pipe(
      select(selectSchemaViewBySid('io.synply.v1.type.indu')),
      filter(Boolean),
      first(),
      map(schema =>
        Object.entries(schema.graphEnum?.vals ?? {}).map(([id, val]) => ({
          id,
          ...val,
        }))
      ),
      switchMap((res: SchemaEnumItem[]) => {
        const labels = res
          .map((ind: SchemaEnumItem) => ind.label)
          .filter(Boolean);
        return this.translateService.getTranslations(labels).pipe(
          take(1),
          map(translations =>
            res.map((ind: SchemaEnumItem) => ({
              ...ind,
              name: translations[ind.label] || ind.description,
            }))
          )
        );
      })
    );
  }

  private setupTermSheetSchemaOptions(): void {
    combineLatest([
      this.productControl.valueChanges.pipe(
        startWith(this.productControl.value)
      ),
      this.industryControl.valueChanges.pipe(
        startWith(this.industryControl.value)
      ),
    ])
      .pipe(
        map(([product, industry]) => ({
          product: typeof product === 'string' ? product : null,
          industry: typeof industry === 'string' ? industry : null,
        })),
        filter(({ product, industry }) => Boolean(product && industry)),
        distinctUntilChanged(
          (prev, curr) =>
            prev.product === curr.product && prev.industry === curr.industry
        ),
        switchMap(({ product, industry }) =>
          this.loadTermSheetSchemaView(product!, industry!).pipe(
            map(schema => ({
              schema,
              sid: this.buildSchemaSid(product!, industry!),
            }))
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ schema, sid }) => {
        this.buildTermSheetSelectOptions(schema, sid);
      });
  }

  private loadTermSheetSchemaView(
    productId: string,
    industryId: string
  ): Observable<SchemaView> {
    const sid = this.buildSchemaSid(productId, industryId);
    const query =
      'io.synply.v1.view.form_admin' + this.getSchemasParamsFromSid(sid);

    this.schemasStore.loadByQuery(query);
    return toObservable(this.schemasStore.schemaByQuery(query), {
      injector: this.injector,
    }).pipe(filter(Boolean), first());
  }

  private buildSchemaSid(
    productId: string,
    industryId: string
  ): Record<string, string> {
    return {
      a: 'io.synply.v1.model.core.agency',
      c: 'io.synply.v1.model.core.common',
      l: productId,
      s: industryId,
      p: 'io.synply.v1.model.derived.participation',
    };
  }

  private getSchemasParamsFromSid(sid: Record<string, string>): string {
    if (!sid || Object.keys(sid).length === 0) return '';
    let params = new HttpParams();
    for (const schema of Object.values(sid)) {
      const property = schema.split('.').reverse()[1];
      params = params.set(property, schema);
    }
    return params.toString().length > 0 ? `?${params.toString()}` : '';
  }

  private buildTermSheetSelectOptions(
    schema: SchemaView,
    sid: Record<string, string>
  ): void {
    if (!schema?.properties) {
      this.termSheetSelectOptions = {};
      this.cdr.markForCheck();
      return;
    }

    const namespaceMap = Object.entries(sid).reduce(
      (acc, [key, value]) => ({ ...acc, [value]: key }),
      {} as Record<string, string>
    );

    const propertiesList = schema.properties.map(cur => {
      const rawId = cur.id;
      let normalizedId = rawId;
      const lastDotIndex = rawId?.lastIndexOf?.('.') ?? -1;
      if (lastDotIndex !== -1) {
        const schemaId = rawId.slice(0, lastDotIndex);
        const property = rawId.slice(lastDotIndex + 1);
        normalizedId = `${namespaceMap[schemaId] || schemaId}.${property}`;
      }
      return { ...cur, rawId, id: normalizedId };
    });

    const fieldSuffixMap: Record<string, string[]> = {
      paymentFrequency: ['c.pmtFrq', 'pmtFrq'],
      index: ['c.indx', 'indx'],
      rateResetFrequency: ['c.rrf', 'rrf'],
      syndicationType: ['c.sTp', 'sTp'],
      underwritingType: ['c.uTp', 'uTp'],
    };

    const labelMatches: Record<string, string[]> = {};

    const normalizeSchemaKey = (value?: string | null) =>
      (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

    const resolveProperty = (suffixes: string[], labelHints: string[] = []) => {
      const matched = propertiesList.find(
        (prop: { id?: string; rawId?: string }) =>
          suffixes.some(suffix => {
            const suffixKey = normalizeSchemaKey(suffix);
            const id = normalizeSchemaKey(prop.id);
            const rawId = normalizeSchemaKey(prop.rawId);
            return (
              id === suffixKey ||
              rawId === suffixKey ||
              id.endsWith(suffixKey) ||
              rawId.endsWith(suffixKey)
            );
          })
      );

      if (matched || labelHints.length === 0) {
        return matched;
      }

      return propertiesList.find(prop => {
        const label =
          `${prop.label || ''} ${prop.description || ''}`.toLowerCase();
        return labelHints.some(hint => label.includes(hint));
      });
    };

    const resolveValues = (property?: {
      v?: any;
      input?: { v?: any; vals?: any; values?: any };
    }) => {
      const raw =
        property?.v ??
        property?.input?.v ??
        property?.input?.vals ??
        property?.input?.values;
      if (Array.isArray(raw)) {
        return raw;
      }
      if (raw?.vals && typeof raw.vals === 'object') {
        return Object.entries(raw.vals).map(([id, value]) => ({
          id,
          ...(typeof value === 'object' ? value : { name: value }),
        }));
      }
      if (raw?.values && typeof raw.values === 'object') {
        return Object.entries(raw.values).map(([id, value]) => ({
          id,
          ...(typeof value === 'object' ? value : { name: value }),
        }));
      }
      if (raw && typeof raw === 'object') {
        return Object.entries(raw).map(([id, value]) => ({
          id,
          ...(typeof value === 'object' ? value : { name: value }),
        }));
      }
      return [];
    };

    const optionRequests = Object.entries(fieldSuffixMap).map(
      ([field, suffixes]) => {
        const property = resolveProperty(suffixes, labelMatches[field] ?? []);
        const values = resolveValues(property);
        if (!Array.isArray(values) || values.length === 0) {
          return of<[string, SchemaEnumItem[]]>([field, []]);
        }
        return this.mapSchemaOptions(values).pipe(
          map(options => [field, options] as [string, SchemaEnumItem[]])
        );
      }
    );

    forkJoin(optionRequests)
      .pipe(take(1))
      .subscribe(entries => {
        this.termSheetSelectOptions = entries.reduce(
          (acc, [field, options]) => ({ ...acc, [field]: options }),
          {}
        );
        this.applyTermSheetSelectDefaults();
        this.cdr.markForCheck();
      });
  }

  private applyTermSheetSelectDefaults(): void {
    const fields: TermSheetSelectField[] = [
      'paymentFrequency',
      'index',
      'rateResetFrequency',
      'syndicationType',
      'underwritingType',
    ];

    let optionsUpdated = false;

    fields.forEach(field => {
      const control = this.termSheetControls[field];
      let options = this.termSheetSelectOptions[field as string] ?? [];
      const rawValue = control?.value;

      if (!control || control.dirty) {
        return;
      }

      if (rawValue === null || rawValue === undefined || rawValue === '') {
        return;
      }

      const match = this.findSelectOptionMatch(rawValue, options);
      if (!match) {
        const fallbackValue = String(rawValue);
        options = [...options, { id: fallbackValue, name: fallbackValue }];
        this.termSheetSelectOptions = {
          ...this.termSheetSelectOptions,
          [field]: options,
        };
        optionsUpdated = true;
        return;
      }
      if (match.id !== rawValue) {
        const optionId = match.id === null ? null : String(match.id);
        this.setTermSheetSelectValue(field, optionId);
      }
    });

    if (optionsUpdated) {
      this.cdr.markForCheck();
    }
  }

  private setTermSheetSelectValue(
    field: TermSheetSelectField,
    value: string | null
  ): void {
    const control = this.termSheetControls[field] as FormControl<string | null>;
    control.setValue(value, { emitEvent: false });
  }

  private findSelectOptionMatch(
    value: unknown,
    options: SchemaEnumItem[]
  ): SchemaEnumItem | null {
    const normalized = this.normalizeSelectValue(value);
    const compactNormalized = normalized.replace(/\s+/g, '');
    if (!normalized) return null;

    const numericValue = Number(value);
    const hasNumeric = Number.isFinite(numericValue);

    const idMatch = options.find(option => {
      if (hasNumeric) {
        const optionNumber = Number(option.id);
        if (Number.isFinite(optionNumber) && optionNumber === numericValue) {
          return true;
        }
      }
      const optionNormalized = this.normalizeSelectValue(option.id);
      if (optionNormalized === normalized) {
        return true;
      }
      if (
        compactNormalized &&
        optionNormalized.replace(/\s+/g, '') === compactNormalized
      ) {
        return true;
      }
      return false;
    });

    if (idMatch) return idMatch;

    const labelMatch = options.find(option => {
      const labelText = [option.name, option.label, option.description].filter(
        Boolean
      );
      return labelText.some(text => {
        const candidate = this.normalizeSelectValue(text);
        const compactCandidate = candidate.replace(/\s+/g, '');
        return (
          candidate === normalized ||
          (compactNormalized && compactCandidate === compactNormalized) ||
          (candidate.length > 3 && candidate.includes(normalized)) ||
          (normalized.length > 3 && normalized.includes(candidate))
        );
      });
    });

    return labelMatch ?? null;
  }

  private normalizeSelectValue(value: unknown): string {
    if (value === null || value === undefined) return '';
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private mapSchemaOptions(values: any[]): Observable<SchemaEnumItem[]> {
    const options = values.map(option => {
      const rawId = option?.id ?? option?.value ?? option;
      const id = rawId === null || rawId === undefined ? rawId : String(rawId);
      return {
        id,
        label: option?.label,
        description: option?.description,
        name: option?.name,
      };
    });

    const labels = options
      .map(option => option.label)
      .filter(Boolean) as string[];

    if (labels.length === 0) {
      return of(
        options.map(option => ({
          ...option,
          name:
            option.name ||
            option.description ||
            option.label ||
            String(option.id),
        }))
      );
    }

    return this.translateService.getTranslations(labels).pipe(
      take(1),
      map(translations =>
        options.map(option => ({
          ...option,
          name:
            translations[option.label as string] ||
            option.name ||
            option.description ||
            option.label ||
            String(option.id),
        }))
      )
    );
  }

  private setupLeadBankFilters(): void {
    this.leadBankStore.loadLeadBanks();
    this.leadBanks$ = toObservable(this.leadBankStore.leadBanks, {
      injector: this.injector,
    });
    this.leadBanksLoading$ = toObservable(this.leadBankStore.leadBanksLoading, {
      injector: this.injector,
    });
    this.validationLoading$ = toObservable(
      this.leadBankStore.validationLoading,
      { injector: this.injector }
    );

    this.filteredLeadBanks$ = combineLatest([
      this.leadBanks$,
      toObservable(this.userStore.mainCompany, { injector: this.injector }),
      this.leadBankControl.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([banks, mainCompany, filterValue]) => {
        const mainCompanyId = mainCompany?._id;
        const filteredBanks = mainCompanyId
          ? banks.filter(bank => bank?.acnId !== mainCompanyId)
          : banks;
        const query =
          typeof filterValue === 'string'
            ? filterValue
            : filterValue?.name || filterValue?.email || '';
        const normalized = query.toLowerCase().trim();
        if (!normalized) {
          return filteredBanks;
        }
        return filteredBanks.filter(bank => {
          const name = (bank?.name || bank?.email || '').toLowerCase();
          return name.includes(normalized);
        });
      })
    );
    toObservable(this.leadBankStore.validationResult, {
      injector: this.injector,
    })
      .pipe(
        takeUntil(this.destroy$),
        filter(res => res !== undefined)
      )
      .subscribe(result => {
        this.handleEmailValidationResult(result);
      });

    this.leadBankControl.valueChanges
      .pipe(startWith(this.leadBankControl.value), takeUntil(this.destroy$))
      .subscribe(val => {
        if (this.isLeadBankOption(val)) {
          this.selectedLeadBank = val;
          if (!this.leadBankEmailControl.value && val.email) {
            this.leadBankEmailControl.setValue(val.email);
          } else if (this.leadBankEmailControl.value) {
            this.validateLeadBankEmail(this.leadBankEmailControl.value, true);
          }
        } else {
          this.selectedLeadBank = null;
          if (this.leadBankEmailControl.value) {
            this.validateLeadBankEmail(this.leadBankEmailControl.value, true);
          }
        }
      });

    this.leadBankEmailControl.valueChanges
      .pipe(
        debounceTime(800),
        distinctUntilChanged(),
        startWith(this.leadBankEmailControl.value),
        takeUntil(this.destroy$)
      )
      .subscribe(value => {
        this.validateLeadBankEmail(value, true);
      });
  }

  displayLeadBank(value: string | LeadBankOption | null): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.name || '';
  }

  private isLeadBankOption(value: unknown): value is LeadBankOption {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const acnId = (value as LeadBankOption).acnId;
    return typeof acnId === 'string' && acnId.trim().length > 0;
  }

  private getLeadBankId(option: LeadBankOption | null): string | null {
    if (!option) {
      return null;
    }
    const acnId = option.acnId;
    if (typeof acnId !== 'string') {
      return null;
    }
    const trimmed = acnId.trim();
    return trimmed ? trimmed : null;
  }

  selectLeadBankOption(option: LeadBankOption): void {
    this.selectedLeadBank = option;
    this.leadBankControl.setValue(option);
    if (!this.leadBankEmailControl.value && option?.email) {
      this.leadBankEmailControl.setValue(option.email);
    }
  }

  submit(): void {
    if (this.isLeadBank) {
      this.submitLeadBank();
      return;
    }

    if (
      !this.nameControl.valid ||
      !this.productControl.valid ||
      !this.industryControl.valid ||
      !this.purchaseAmountControl.valid ||
      this.leadBankGroup.invalid
    ) {
      this.markRequiredFieldsTouched();
      return;
    }

    const proceed = () => {
      const payload = this.buildPayload();
      this.leadBankStore.submitExternalInvest(payload);
    };

    const email = this.leadBankEmailControl.value;
    if (email) {
      const trimmed = email.trim();
      const hasAt = trimmed.includes('@');
      const hasDot = trimmed.includes('.');
      if (!hasAt || !hasDot) {
        this.setEmailError(this.getInvalidEmailMessage());
        this.resetEmailHelpers();
        return;
      }

      if (!this.isLeadBankDomainMatch(trimmed)) {
        this.setEmailError(this.translateKey(this.domainMismatchKey));
        this.resetEmailHelpers();
        this.leadBankEmailValid = false;
        return;
      }

      this.leadBankStore.clearValidation();
      this.leadBankStore.validateEmail(trimmed);
      toObservable(this.leadBankStore.validationResult, {
        injector: this.injector,
      })
        .pipe(
          filter(res => res !== null),
          take(1)
        )
        .subscribe(res => {
          const isAcceptable = this.handleEmailValidationResult(res, true);
          if (isAcceptable) {
            proceed();
          }
        });
    } else {
      proceed();
    }
  }

  get termSheetControls(): TermSheetFormControls {
    return this.termSheetForm.controls;
  }

  get canUploadTermSheet(): boolean {
    const baseValid =
      this.nameControl.valid &&
      this.productControl.valid &&
      this.industryControl.valid;

    if (this.isLeadBank) {
      const ndaValid =
        this.ndaWorkflowControl.value !== NdaWorkflow.Personal ||
        this.ndaTemplateControl.valid;
      return baseValid && this.amountGroup.valid && ndaValid;
    }

    return (
      baseValid &&
      this.purchaseAmountControl.valid &&
      this.leadBankControl.valid
    );
  }

  get hasTermSheetPreview(): boolean {
    return Boolean(this.termSheetFiles[0] || this.termSheetDocId);
  }

  openTermSheetPreview(): void {
    const file = this.termSheetFiles[0];
    if (file) {
      const url = URL.createObjectURL(file);
      this.revokeTermSheetPreviewUrl();
      this.termSheetPreviewUrl = url;
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => this.revokeTermSheetPreviewUrl(), 60000);
      return;
    }

    if (this.termSheetDocId) {
      window.open(
        this.buildTermSheetDocUrl(this.termSheetDocId),
        '_blank',
        'noopener'
      );
    }
  }

  openTermSheetFileSelector(): void {
    if (!this.canUploadTermSheet) {
      this.markTermSheetUploadBlocked();
      return;
    }
    this.termSheetInput?.nativeElement.click();
  }

  onTermSheetFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!this.canUploadTermSheet) {
      if (input) {
        input.value = '';
      }
      this.markTermSheetUploadBlocked();
      return;
    }
    if (input?.files?.length) {
      this.termSheetUploadError = false;
      this.termSheetUploadBlocked = false;
      this.resetTermSheetUpload();
      this.termSheetFiles = Array.from(input.files);
      if (this.termSheetFiles.length > 0) {
        this.startTermSheetUploadProcess();
      }
    }
  }

  deleteTermSheetFile(file: File): void {
    this.termSheetFiles = this.termSheetFiles.filter(f => f !== file);
    if (this.termSheetFiles.length === 0) {
      this.clearTermSheetUploadState();
    }
  }

  handleTermSheetDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.termSheetDragCounter = 0;

    if (!this.canUploadTermSheet) {
      this.markTermSheetUploadBlocked();
      return;
    }

    const files = event.dataTransfer?.files;
    if (files && files.length) {
      this.termSheetUploadError = false;
      this.termSheetUploadBlocked = false;
      this.resetTermSheetUpload();
      this.termSheetFiles = Array.from(files);
      if (this.termSheetInput?.nativeElement) {
        this.termSheetInput.nativeElement.value = '';
      }
      if (this.termSheetFiles.length > 0) {
        this.startTermSheetUploadProcess();
      }
    }
  }

  handleTermSheetDragEnter(event: DragEvent): void {
    event.preventDefault();
    if (!this.canUploadTermSheet) {
      return;
    }
    this.termSheetDragCounter++;
  }

  handleTermSheetDragLeave(event: DragEvent): void {
    event.preventDefault();
    if (!this.canUploadTermSheet) {
      return;
    }
    this.termSheetDragCounter = Math.max(0, this.termSheetDragCounter - 1);
  }

  handleTermSheetDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.canUploadTermSheet) {
      return;
    }
  }

  private startTermSheetUploadProcess(): void {
    if (this.termSheetFiles.length === 0) {
      return;
    }

    this.termSheetUploadBlocked = false;
    this.termSheetWrongDocument = false;
    this.setProcessingPanelState(true);
    this.termSheetStep = 'processing';
    this.updateDialogWidth('base');
    this.isTermSheetUploading$.next(true);
    this.uploadTermSheetDocument();
  }

  private uploadTermSheetDocument(): void {
    const file = this.termSheetFiles[0];
    if (!file) {
      return;
    }

    this.documentsStore.resetCorporateNda();
    this.termSheetDocId = null;

    const handleUploadFailure = () => {
      this.termSheetUploadError = false;
      this.termSheetWrongDocument = true;
      this.clearTermSheetSelection();
      this.scrollToTermSheetError();
      this.termSheetStep = 'base';
      this.updateDialogWidth('base');
      this.isTermSheetUploading$.next(false);
      this.setProcessingPanelState(false);
      this.cdr.markForCheck();
    };

    const formData = new FormData();
    formData.append('data', file);
    formData.append('_ks', 'ndaTemplate={{<<crud-insert>>}}');
    formData.append('doc.dtp', '450');
    formData.append('_acc', '200');
    formData.append('_dsc', file.name);

    const productId =
      typeof this.productControl.value === 'string'
        ? this.productControl.value
        : null;
    const industryId =
      typeof this.industryControl.value === 'string'
        ? this.industryControl.value
        : null;
    if (productId && industryId) {
      formData.append('doc.sid.l', productId);
      formData.append('doc.sid.s', industryId);
    }

    this.documentsStore
      .uploadDocuments({
        data: formData,
        attr: DocumentType.CORPORATE_NDA,
      })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        error: () => {
          if (this.termSheetStep === 'processing') {
            handleUploadFailure();
          }
        },
      });
  }

  private resolveTermSheetDocId(docId?: string | null): void {
    if (!docId || this.termSheetDocId) {
      return;
    }
    this.termSheetDocId = docId;
    this.subscribeToTermSheetDocMeta(docId);
  }

  private subscribeToTermSheetDocMeta(docId: string): void {
    if (!docId) {
      this.termSheetUploadError = false;
      this.termSheetWrongDocument = true;
      this.clearTermSheetSelection();
      this.scrollToTermSheetError();
      this.isTermSheetUploading$.next(false);
      this.setProcessingPanelState(false);
      return;
    }

    if (this.termSheetMysid) {
      this.termSheetStore.disconnect(this.termSheetMysid);
    }

    this.termSheetMysid = `term_sheet_${docId}_${Date.now()}`;
    const mysid = this.termSheetMysid;

    this.termSheetStore.connect({
      mysid,
      q: [
        {
          k: 'docId',
          op: 'EQ',
          v: [docId],
        },
      ],
    });

    const docMetaResponse$ = this.termSheetDocMeta$.pipe(
      map(docMeta => docMeta[mysid]),
      filter(Boolean)
    );

    const timeout$ = timer(TERM_SHEET_EXTRACTION_TIMER);

    race(docMetaResponse$, timeout$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.handleTermSheetDocMetaResponse(value as DocMetaResponse);
        } else {
          this.termSheetUploadError = false;
          this.termSheetWrongDocument = true;
          this.clearTermSheetSelection();
          this.scrollToTermSheetError();
          this.termSheetStep = 'base';
          this.updateDialogWidth('base');
        }

        this.isTermSheetUploading$.next(false);
        this.setProcessingPanelState(false);
        this.cdr.markForCheck();
      });
  }

  private handleTermSheetDocMetaResponse(response: DocMetaResponse): void {
    this.termSheetDocMeta = response;
    const container = response?.containers?.[0];
    if (!container?.data || container.data['isValid'] !== 'true') {
      this.termSheetUploadError = false;
      this.termSheetWrongDocument = true;
      this.termSheetStep = 'base';
      this.updateDialogWidth('base');
      this.clearTermSheetSelection();
      this.scrollToTermSheetError();
      return;
    }

    this.termSheetData = container.data;
    this.applyTermSheetData(container.data);
    this.termSheetStep = 'details';
    this.updateDialogWidth('details');
    this.scheduleTermSheetPreview();
  }

  private applyTermSheetData(data: Record<string, string | null>): void {
    this.termSheetForm.patchValue(
      buildTermSheetPatch(data, {
        parseDateValue: value => this.parseDateValue(value),
        parseNumber: value => this.parseNumber(value),
        parsePercent: value => this.parsePercentValue(value),
        toStringValue: value => this.toStringValue(value),
      })
    );

    const selectFields: TermSheetSelectField[] = [
      'paymentFrequency',
      'rateResetFrequency',
      'syndicationType',
      'underwritingType',
      'index',
    ];

    selectFields.forEach(field => {
      const containerKey = TERM_SHEET_FIELD_MAP[field];
      const rawValue = containerKey ? data[containerKey] : undefined;
      if (rawValue === undefined || rawValue === null || rawValue === '') {
        return;
      }
      this.setTermSheetSelectValue(field, this.toStringValue(rawValue));
    });

    this.applyTermSheetSelectDefaults();
    this.cdr.markForCheck();
  }

  private resetTermSheetUpload(): void {
    this.isTermSheetUploading$.next(false);
    this.termSheetStep = 'base';
    this.setProcessingPanelState(false);
    this.termSheetDocMeta = null;
    this.termSheetData = null;
    this.termSheetDocId = null;
    this.termSheetUploadError = false;
    this.termSheetUploadBlocked = false;
    this.termSheetWrongDocument = false;
    this.updateDialogWidth('base');
    this.clearTermSheetPreview();
    this.termSheetForm.reset();
    this.documentsStore.resetCorporateNda();
    if (this.termSheetMysid) {
      this.termSheetStore.disconnect(this.termSheetMysid);
      this.termSheetMysid = null;
    }
  }

  private clearTermSheetUploadState(): void {
    this.resetTermSheetUpload();
    this.termSheetFiles = [];
    this.termSheetDragCounter = 0;
    this.isTermSheetUploading$.next(false);
    if (this.termSheetInput?.nativeElement) {
      this.termSheetInput.nativeElement.value = '';
    }
  }

  private clearTermSheetSelection(): void {
    this.termSheetFiles = [];
    this.termSheetDragCounter = 0;
    this.termSheetDocMeta = null;
    this.termSheetData = null;
    this.termSheetDocId = null;
    this.clearTermSheetPreview();
    if (this.termSheetInput?.nativeElement) {
      this.termSheetInput.nativeElement.value = '';
    }
    if (this.termSheetMysid) {
      this.termSheetStore.disconnect(this.termSheetMysid);
      this.termSheetMysid = null;
    }
  }

  private scrollToTermSheetError(): void {
    window.setTimeout(() => {
      const target = this.termSheetErrorMessage?.nativeElement;
      if (!target) {
        return;
      }
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  private scheduleTermSheetPreview(): void {
    window.setTimeout(() => {
      this.loadTermSheetPreview();
    }, 0);
  }

  private loadTermSheetPreview(): void {
    const container = this.termSheetPreviewContainer?.nativeElement;
    if (!container) {
      return;
    }

    this.clearTermSheetPreview();
    const file = this.termSheetFiles[0];
    if (file) {
      this.renderTermSheetFile(file);
      return;
    }

    if (this.termSheetDocId) {
      this.renderTermSheetDoc(this.termSheetDocId);
    }
  }

  private renderTermSheetFile(file: File): void {
    const container = this.termSheetPreviewContainer?.nativeElement;
    if (!container) {
      return;
    }

    if (
      file.type !== 'application/pdf' &&
      !file.name.toLowerCase().endsWith('.pdf')
    ) {
      this.termSheetPreviewError = true;
      this.cdr.markForCheck();
      return;
    }

    if (file.size > 150 * 1024 * 1024) {
      this.termSheetPreviewError = true;
      this.cdr.markForCheck();
      return;
    }

    this.termSheetPreviewLoading = true;
    this.termSheetPreviewError = false;
    this.cdr.markForCheck();

    const fileReader = new FileReader();

    new Observable<Uint8Array>(observer => {
      fileReader.onload = () => {
        try {
          const arrayBuffer = fileReader.result as ArrayBuffer;
          if (!arrayBuffer || arrayBuffer.byteLength === 0) {
            observer.error('Empty or invalid file buffer');
            return;
          }

          const typedArray = new Uint8Array(arrayBuffer);
          observer.next(typedArray);
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      };

      fileReader.onerror = error => {
        observer.error(error);
      };

      fileReader.readAsArrayBuffer(file);
    })
      .pipe(
        switchMap((typedArray: Uint8Array) =>
          from(getDocument({ data: typedArray }).promise)
        ),
        tap(pdf => {
          this.termSheetPdfDocument = pdf;
        }),
        filter(pdf => !!pdf),
        switchMap(pdf => this.renderTermSheetPdf(pdf, container)),
        catchError(() => {
          this.termSheetPreviewLoading = false;
          this.termSheetPreviewError = true;
          this.cdr.markForCheck();
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private renderTermSheetDoc(docId: string): void {
    const container = this.termSheetPreviewContainer?.nativeElement;
    if (!container) {
      return;
    }

    this.termSheetPreviewLoading = true;
    this.termSheetPreviewError = false;
    this.cdr.markForCheck();

    const url = this.buildTermSheetDocUrl(docId);

    from(getDocument({ url, withCredentials: true }).promise)
      .pipe(
        tap(pdf => {
          this.termSheetPdfDocument = pdf;
        }),
        filter(pdf => !!pdf),
        switchMap(pdf => this.renderTermSheetPdf(pdf, container)),
        catchError(() => {
          this.termSheetPreviewLoading = false;
          this.termSheetPreviewError = true;
          this.cdr.markForCheck();
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private renderTermSheetPdf(
    pdf: PDFDocumentProxy,
    container: HTMLDivElement
  ): Observable<unknown> {
    return from(Array.from({ length: pdf.numPages }, (_, i) => i + 1)).pipe(
      concatMap(pageNumber => {
        const canvas = document.createElement('canvas');
        canvas.style.display = 'block';
        canvas.style.marginBottom = '2px';
        container.appendChild(canvas);

        return defer(() => from(pdf.getPage(pageNumber))).pipe(
          mergeMap((page: PDFPageProxy) => {
            const baseViewport = page.getViewport({ scale: 1.0 });
            const scale = this.getTermSheetPreviewScale(
              container,
              baseViewport.width
            );
            const viewport = page.getViewport({ scale });
            const outputScale = window.devicePixelRatio || 1;
            canvas.style.width = `${viewport.width}px`;
            canvas.style.height = `${viewport.height}px`;
            canvas.width = Math.floor(viewport.width * outputScale);
            canvas.height = Math.floor(viewport.height * outputScale);
            const context = canvas.getContext('2d');
            if (!context) {
              return of(null);
            }

            return from(
              page.render({
                canvasContext: context,
                viewport,
                transform:
                  outputScale === 1
                    ? undefined
                    : [outputScale, 0, 0, outputScale, 0, 0],
              }).promise
            );
          }),
          catchError(() => of(null))
        );
      }),
      takeLast(1),
      tap(() => {
        this.termSheetPreviewLoading = false;
        this.cdr.markForCheck();
      }),
      takeUntil(this.destroy$)
    );
  }

  private getTermSheetPreviewScale(
    container: HTMLDivElement,
    viewportWidth: number
  ): number {
    if (!container || !viewportWidth) {
      return 1;
    }

    const styles = window.getComputedStyle(container);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;
    const availableWidth = container.clientWidth - paddingLeft - paddingRight;

    if (!availableWidth || availableWidth <= 0) {
      return 1;
    }

    const maxScale = window.innerWidth >= 1400 ? 1.5 : 1.35;
    return Math.min(maxScale, availableWidth / viewportWidth);
  }

  private clearTermSheetPreview(): void {
    if (this.termSheetPreviewContainer?.nativeElement) {
      this.termSheetPreviewContainer.nativeElement.innerHTML = '';
    }
    this.termSheetPreviewLoading = false;
    this.termSheetPreviewError = false;
    this.termSheetPdfDocument = null;
    this.revokeTermSheetPreviewUrl();
  }

  private revokeTermSheetPreviewUrl(): void {
    if (this.termSheetPreviewUrl) {
      URL.revokeObjectURL(this.termSheetPreviewUrl);
      this.termSheetPreviewUrl = null;
    }
  }

  private buildTermSheetDocUrl(docId: string): string {
    return `${this.baseUrl}/api/v1/service/doc/${docId}`;
  }

  private updateDialogWidth(step: 'base' | 'details'): void {
    const width =
      step === 'details' ? this.detailsDialogWidth : this.baseDialogWidth;
    this.dialogRef.updateSize(width);
  }

  private setupTermSheetUploadGate(): void {
    merge(
      this.nameControl.statusChanges,
      this.productControl.statusChanges,
      this.industryControl.statusChanges,
      this.purchaseAmountControl.statusChanges,
      this.leadBankControl.statusChanges
    )
      .pipe(startWith(null), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.canUploadTermSheet && this.termSheetUploadBlocked) {
          this.termSheetUploadBlocked = false;
          this.cdr.markForCheck();
        }
      });
  }

  private markTermSheetUploadBlocked(): void {
    if (!this.termSheetUploadBlocked) {
      this.termSheetUploadBlocked = true;
      this.markRequiredFieldsTouched();
      if (this.isLeadBank) {
        this.ndaWorkflowControl.markAsTouched();
        this.ndaTemplateControl.markAsTouched();
        this.ndaWorkflowControl.updateValueAndValidity({ onlySelf: true });
        this.ndaTemplateControl.updateValueAndValidity({ onlySelf: true });
      }
      this.cdr.markForCheck();
    }
  }

  private markRequiredFieldsTouched(): void {
    const controls = [
      this.nameControl,
      this.productControl,
      this.industryControl,
      this.purchaseAmountControl,
      this.leadBankControl,
      this.leadBankEmailControl,
    ];
    controls.forEach(control => {
      control.markAsTouched();
      control.updateValueAndValidity({ onlySelf: true });
    });
    this.leadBankGroup.markAllAsTouched();
    this.cdr.markForCheck();
  }

  private markLeadBankRequiredFieldsTouched(): void {
    [this.nameControl, this.productControl, this.industryControl].forEach(c => {
      c.markAsTouched();
      c.updateValueAndValidity({ onlySelf: true });
    });
    this.amountGroup.markAllAsTouched();
    this.ndaWorkflowControl.markAsTouched();
    this.ndaTemplateControl.markAsTouched();
    this.cdr.markForCheck();
  }

  private setProcessingPanelState(isActive: boolean): void {
    if (this.isProcessingPanelActive === isActive) {
      return;
    }
    if (isActive) {
      this.dialogRef.addPanelClass('create-participant-dialog--processing');
    } else {
      this.dialogRef.removePanelClass('create-participant-dialog--processing');
    }
    this.isProcessingPanelActive = isActive;
  }

  submitLeadBank(): void {
    const isNdaTemplateMissing =
      this.ndaWorkflowControl.value === NdaWorkflow.Personal &&
      !this.ndaTemplateControl.valid;
    const isInvalid =
      !this.nameControl.valid ||
      !this.productControl.valid ||
      !this.industryControl.valid ||
      !this.amountGroup.valid ||
      !this.ndaWorkflowControl.valid ||
      isNdaTemplateMissing;

    if (isInvalid) {
      // Reuse existing behavior (all core fields) and add lead-bank specifics
      this.markRequiredFieldsTouched();
      this.markLeadBankRequiredFieldsTouched();
      const messageKey = isNdaTemplateMissing
        ? 'GNR_NDA_TEMPLATE_REQUIRED'
        : 'ERR_FIELD_REQUIRED';
      this.snackBar.showSnackBar({
        type: 'ErrorMessage',
        message: messageKey,
      } as ErrorSnackBarData);
      return;
    }

    this.leadBankSaving$.next(true);
    const item = this.buildLeadBankItemPayload();
    this.itemStore.createItem(item);
  }

  private buildLeadBankItemPayload(): Item {
    const dealName = this.nameControl.value;
    // Exclude UI-only flags (isLeadBank, entryMode) from the persisted Item
    // payload — they're not backend fields and some backend validators reject
    // unknown properties.
    const {
      isLeadBank: _isLeadBank,
      entryMode: _entryMode,
      ...carryOver
    } = this.data || {};
    const item: Item = { ...new Item(), ...carryOver };
    item.name = dealName;
    const productId =
      typeof this.productControl.value === 'string'
        ? this.productControl.value
        : null;
    const industryId =
      typeof this.industryControl.value === 'string'
        ? this.industryControl.value
        : null;
    item.data = {
      sid: {
        a: 'io.synply.v1.model.core.agency',
        c: 'io.synply.v1.model.core.common',
        f: 'io.synply.v1.model.core.fees',
        ...(productId && { l: productId }),
        s: productId,
        i: industryId,
        p: 'io.synply.v1.model.derived.participation',
      },
      data: {
        'c.indu': industryId,
        'c.pr': productId,
        'c.oAmt': this.amountGroup.get('originalAmount')?.value,
        'p.minHold': this.amountGroup.get('minHold')?.value,
      },
    };

    const termSheetPayload = this.buildTermSheetPayload();
    if (termSheetPayload && Object.keys(termSheetPayload).length > 0) {
      item.data.data = { ...item.data.data, ...termSheetPayload };
    }

    item.prTp = ItemTypeBitMasks.LOAN;
    item.myPar = {
      sts: 7,
      role: 1000,
      committedVal: {
        a: this.amountGroup.get('agentHold')?.value,
        c: 'USD',
      },
    };
    item.ndaWorkFlow = this.ndaWorkflowControl.value;
    this.setLeadBankTemplateIds(item);
    if (this.data?.stg) {
      item.stg = this.data.stg;
    }
    if (this.data?.pid) {
      item.pid = this.data.pid;
    }
    return item;
  }

  private setLeadBankTemplateIds(item: Item): void {
    if (this.ndaTemplateControl.value) {
      const templateId = this.extractTemplateId(this.ndaTemplateControl.value);
      if (templateId) {
        item.ndaDtaId = templateId;
      }
      item.ndaTplId = this.ndaTemplateControl.value._id;
    }
    if (this.folderTemplateControl.value) {
      const templateId = this.extractTemplateId(
        this.folderTemplateControl.value
      );
      if (templateId) {
        item.templateStructureId = templateId;
      }
    }
    const workflowTemplateIds = this.workflowTemplateControl.value ?? [];
    if (workflowTemplateIds.length > 0) {
      item.templateWorkflowIds = workflowTemplateIds;
    }
  }

  get originalAmountControl(): FormControl<number | null> {
    return this.amountGroup.get('originalAmount') as FormControl<number | null>;
  }

  get agentHoldControl(): FormControl<number | null> {
    return this.amountGroup.get('agentHold') as FormControl<number | null>;
  }

  get minHoldControl(): FormControl<number | null> {
    return this.amountGroup.get('minHold') as FormControl<number | null>;
  }

  isPersonalWorkflowSelected(): boolean {
    return this.ndaWorkflowControl.value === NdaWorkflow.Personal;
  }

  private parseNumber(value: unknown): number | null {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const sanitized = value.replace(/[^\d.-]/g, '');
      if (!sanitized) return null;
      const parsed = Number(sanitized);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  private parsePercentValue(value: unknown): number | null {
    const parsed = this.parseNumber(value);
    if (parsed === null) return null;
    const abs = Math.abs(parsed);
    if (abs <= 1) return parsed;
    if (abs <= 100) return parsed / 100;
    if (abs <= 10000) return parsed / 10000;
    return parsed / 1000000;
  }

  private parseDateValue(value: unknown): string | null {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
      }
      const mdyParsed = parse(value, 'M/d/yyyy', new Date());
      if (isValid(mdyParsed)) {
        return format(mdyParsed, 'yyyy-MM-dd');
      }
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString().slice(0, 10);
      }
    }
    return null;
  }

  private toStringValue(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    return String(value);
  }

  private buildPayload(): any {
    const leadBank =
      this.selectedLeadBank ||
      (typeof this.leadBankControl.value === 'object'
        ? this.leadBankControl.value
        : null);

    const acnId = this.getLeadBankId(leadBank);

    const sid = {
      a: 'io.synply.v1.model.core.agency',
      c: 'io.synply.v1.model.core.common',
      c1: 'io.synply.v1.model.core.collateral',
      t: 'io.synply.v1.model.product.term',
      s: this.industryControl.value,
      l: this.productControl.value,
      p: 'io.synply.v1.model.derived.participation',
    };

    const payload: any = {
      indicatedVal: {
        a: this.parseNumber(this.purchaseAmountControl.value) || 0,
        c: 'USD',
      },
      asset: {
        name: this.nameControl.value,
        cd: this.nameControl.value,
        side: 'privateCopy',
        data: {
          sid,
          data: {
            'c.indu': this.industryControl.value,
            'c.pr': this.productControl.value,
          },
        },
      },
    };

    const termSheetPayload = this.buildTermSheetPayload();
    if (termSheetPayload) {
      payload.asset.data.data = {
        ...payload.asset.data.data,
        ...termSheetPayload,
      };
    }

    if (acnId) {
      payload.acnId = acnId;
    }

    if (this.leadBankEmailControl.value) {
      payload.email = this.leadBankEmailControl.value;
    }

    this.setTemplateIds(payload);
    return payload;
  }

  private buildTermSheetPayload(): Record<string, unknown> | null {
    const formValue = this.termSheetForm.value;
    const payload: Record<string, unknown> = {};
    Object.entries(TERM_SHEET_FIELD_MAP).forEach(([field, target]) => {
      const value = (formValue as any)[field];
      if (value !== null && value !== undefined && value !== '') {
        payload[target] = value;
      }
    });

    return Object.keys(payload).length > 0 ? payload : null;
  }

  close(): void {
    this.dialogRef.close();
  }

  compareLeadBank = (
    a: LeadBankOption | null,
    b: LeadBankOption | null
  ): boolean => {
    return Boolean(a?.acnId && b?.acnId && a.acnId === b.acnId);
  };

  applyEmailSuggestion(email: string): void {
    if (!email) return;
    this.leadBankEmailControl.setValue(email);
    this.setEmailError(null);
    this.leadBankEmailValid = true;
    this.resetEmailHelpers();
  }

  private setEmailError(message: unknown, markInvalid = true): void {
    const normalizedMessage = this.normalizeValidationMessage(message);
    const nextMessage =
      normalizedMessage ??
      (message === null ? null : this.translateKey(this.invalidEmailKey));
    this.leadBankValidationMessage.next(nextMessage);
    if (nextMessage && markInvalid) {
      this.leadBankEmailControl.setErrors({
        ...(this.leadBankEmailControl.errors || {}),
        server: true,
      });
      this.leadBankEmailControl.markAsTouched();
    } else {
      this.leadBankEmailControl.setErrors(null);
    }
  }

  private validateLeadBankEmail(
    value: string | null,
    runServerValidation: boolean
  ): void {
    this.leadBankEmailValid = false;
    this.setEmailError(null);
    this.resetEmailHelpers();
    this.leadBankDomainMismatch = false;

    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed) return;

    const hasAt = trimmed.includes('@');
    const hasDot = trimmed.includes('.');
    if (!hasAt || !hasDot) {
      this.setEmailError(this.getInvalidEmailMessage());
      return;
    }

    if (!this.isLeadBankDomainMatch(trimmed)) {
      this.setEmailError(this.translateKey(this.domainMismatchKey));
      this.leadBankDomainMismatch = true;
      return;
    }

    if (!runServerValidation) return;
    this.leadBankStore.validateEmail(trimmed);
  }

  private isLeadBankDomainMatch(email: string): boolean {
    const domains = this.getLeadBankDomains(this.selectedLeadBank);
    if (domains.length === 0) {
      return true;
    }

    const emailDomain = this.extractEmailDomain(email);
    if (!emailDomain) {
      return true;
    }

    return domains.some(domain => {
      return emailDomain === domain || emailDomain.endsWith(`.${domain}`);
    });
  }

  private extractEmailDomain(email: string): string | null {
    if (!email) return null;
    const atIndex = email.lastIndexOf('@');
    if (atIndex === -1) return null;
    return this.normalizeDomain(email.slice(atIndex + 1));
  }

  private normalizeDomain(domain: string): string | null {
    if (!domain) return null;
    const normalized = domain
      .trim()
      .toLowerCase()
      .replace(/^@/, '')
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
    return normalized || null;
  }

  private getLeadBankDomains(option: LeadBankOption | null): string[] {
    if (!option) return [];
    const rawDomains = (option.raw as any)?.domains;
    const domains = [
      ...(Array.isArray(option.domains) ? option.domains : []),
      ...(Array.isArray(rawDomains) ? rawDomains : []),
    ];

    const normalized = domains
      .filter((domain): domain is string => typeof domain === 'string')
      .map(domain => this.normalizeDomain(domain))
      .filter((domain): domain is string => Boolean(domain));

    return Array.from(new Set(normalized));
  }

  private resetEmailHelpers(): void {
    this.suggestedEmails = [];
    this.correctedEmail = null;
  }

  private handleEmailValidationResult(
    result: any,
    allowDomainCorrectedProceed = false
  ): boolean {
    if (this.leadBankDomainMismatch) {
      return false;
    }

    if (!result) {
      this.setEmailError(null);
      this.leadBankEmailValid = false;
      this.resetEmailHelpers();
      return false;
    }

    if (result.sts === 'ok') {
      this.setEmailError(null);
      this.resetEmailHelpers();
      this.leadBankEmailValid = true;
      return true;
    }

    if (
      result.sts === 'suggestions' &&
      Array.isArray(result.existingEmailMatches) &&
      result.existingEmailMatches.length > 0
    ) {
      this.setEmailError(this.translateKey('GNR_SUGGESTIONS'), false);
      this.suggestedEmails = result.existingEmailMatches;
      this.correctedEmail = null;
      this.leadBankEmailValid = true;
      return true;
    }

    if (result.sts === 'domain-corrected' && result.correctedEmail) {
      this.setEmailError(this.translateKey('GNR_CORRECTED_EMAIL'), false);
      this.correctedEmail = result.correctedEmail;
      this.suggestedEmails = [];
      this.leadBankEmailValid = true;
      return allowDomainCorrectedProceed;
    }

    this.setEmailError(
      result?.message || this.translateKey(this.invalidEmailKey)
    );
    this.resetEmailHelpers();
    this.leadBankEmailValid = false;
    return false;
  }

  private translateKey(key: string): string {
    const translated = this.translateService.instant(key);
    return translated && translated !== key ? translated : key;
  }

  private getInvalidEmailMessage(): string {
    return this.translateKey(this.invalidEmailKey);
  }

  private normalizeValidationMessage(message: unknown): string | null {
    if (message === null || message === undefined) {
      return null;
    }
    if (typeof message === 'string') {
      return message;
    }
    if (typeof message === 'number' || typeof message === 'boolean') {
      return String(message);
    }
    if (typeof message === 'object') {
      const candidate =
        (message as any).msg ||
        (message as any).message ||
        (message as any).key ||
        (message as any).error;
      if (typeof candidate === 'string') {
        return candidate;
      }
    }
    try {
      return JSON.stringify(message);
    } catch {
      return String(message);
    }
  }

  private extractErrorMessage(error: any): string | null {
    const attrs = error?.error?.sts?.attrs || error?.sts?.attrs;
    const attrErrors = Object.keys(attrs ?? {}).map(key => ({
      key,
      ...(attrs as any)[key],
    }));
    const main = attrErrors.find(err => err.main) || attrErrors[0] || undefined;
    return (
      main?.msg || main?.key || error?.error?.res || error?.message || null
    );
  }

  private resolveInvestmentHint(): void {
    const translated = this.translateService.instant(
      'GNR_PURCHASE_AMOUNT_HINT'
    );
    this.investmentHint =
      translated && translated !== 'GNR_PURCHASE_AMOUNT_HINT'
        ? translated
        : this.defaultInvestmentHint;
  }

  private setTemplateIds(payload: any): void {
    if (this.folderTemplateControl.value) {
      const templateId = this.extractTemplateId(
        this.folderTemplateControl.value
      );
      if (templateId) {
        payload.asset = payload.asset || {};
        payload.asset.templateStructureId = templateId;
      }
    }
  }

  private extractTemplateId(template: DocumentTemplate): string | null {
    let templateData = template.history?.[0];

    if (!templateData && template.data && template.data.length > 0) {
      templateData = template.data[0];
    }

    if (templateData && templateData._id && templateData._vts) {
      return `${templateData._id}.${templateData._vts}`;
    }

    if (template._id) {
      const latestVersionData = template.dataMeta?.templateData?.[0];
      if (latestVersionData) {
        return `${latestVersionData.id}.${latestVersionData.vts}`;
      }
    }

    return null;
  }
}
